// src/pages/hotel-bookings/components/hotelBookingUi.tsx
//
// Small presentational pieces shared by the hotel booking list and detail pages.

import type { ReactNode } from "react";

function cn(...classes: Array<string | false | null | undefined>) {
  return classes.filter(Boolean).join(" ");
}

const PILL = "inline-flex rounded-full border px-2 py-0.5 text-xs font-medium";

export function BookingStatusPill({ status }: { status: string }) {
  const s = String(status || "").toUpperCase();
  const cls =
    s === "CONFIRMED"
      ? "border-emerald-200 bg-emerald-50 text-emerald-800"
      : s === "ON_HOLD"
      ? "border-violet-200 bg-violet-50 text-violet-800"
      : s === "CANCELLED" || s === "REFUNDED"
      ? "border-red-200 bg-red-50 text-red-700"
      : "border-gray-200 bg-gray-50 text-gray-700";
  const label = s === "ON_HOLD" ? "On Hold" : s.replace(/_/g, " ");
  return <span className={cn(PILL, cls)}>{label || "—"}</span>;
}

/** The latest cancellation attempt, when it is not simply "cancelled". */
export function CancellationStatusPill({ status }: { status: string }) {
  const s = String(status || "").toUpperCase();
  const cls =
    s === "PENDING" || s === "SUBMITTING"
      ? "border-amber-200 bg-amber-50 text-amber-800"
      : s === "CANCELLED"
      ? "border-red-200 bg-red-50 text-red-700"
      : "border-gray-200 bg-gray-50 text-gray-700";
  const label =
    s === "PENDING" || s === "SUBMITTING"
      ? "Cancellation pending"
      : s === "REJECTED"
      ? "Cancellation rejected"
      : s === "FAILED"
      ? "Cancellation failed"
      : s === "CANCELLED"
      ? "Cancelled"
      : s;
  return <span className={cn(PILL, cls)}>{label}</span>;
}

export function Card({
  title,
  right,
  children,
}: {
  title: string;
  right?: ReactNode;
  children: ReactNode;
}) {
  return (
    <section className="overflow-hidden rounded-2xl border border-gray-200 bg-white">
      <div className="flex items-center justify-between gap-3 border-b border-gray-100 px-4 py-3">
        <h2 className="text-sm font-semibold text-gray-900">{title}</h2>
        {right}
      </div>
      <div className="px-4 py-3">{children}</div>
    </section>
  );
}

/** A label/value line; values wrap rather than overflow (long refs, emails). */
export function Row({ label, value, strong }: { label: string; value: ReactNode; strong?: boolean }) {
  return (
    <div className="flex items-start justify-between gap-4 py-1.5 text-sm">
      <dt className="shrink-0 text-gray-500">{label}</dt>
      <dd
        className={cn(
          "text-right [overflow-wrap:anywhere]",
          strong ? "font-semibold text-gray-900" : "text-gray-900"
        )}
      >
        {value === null || value === undefined || value === "" ? "—" : value}
      </dd>
    </div>
  );
}
